/**
 * AB#1797 (CR-2026-05-29) — Appointments List V2 service layer.
 *
 * Endpoints (gated by REACT_APP_APPOINTMENTS_LIST_V2_ENABLED):
 *   GET  /salon-owner/appointments/list-v2  — paginated + filtered list
 *   POST /salon-owner/appointments/{id}/mark-paid  — staff-on-behalf path
 *
 * PDPL contract: two DTO shapes per role (Owner = full; Staff = PII-stripped).
 * The response carries `itemsForOwner` OR `itemsForStaff`; never both.
 * FE reads the appropriate list based on the caller's role from UserContext.
 */

import axios from 'axios';

// Mirror the codebase convention (`src/services/api.ts`): use the env var when
// the build pipeline injects it, otherwise fall back to the dev BE URL with the
// /api prefix baked in. Using `?? ''` caused the deployed SWA bundle to emit
// relative /api/... paths that hit the static-site host instead of the BE.
// The fallback already ends with /api, so the request paths below DROP the
// leading /api/ that they were originally written with.
const API_BASE = process.env.REACT_APP_API_BASE_URL
    || 'https://devanza-dev-backend.azurewebsites.net/api';

// ─────────────────────────────────────────────────────────────────────────────
// DTO types — mirror the BE contracts exactly
// ─────────────────────────────────────────────────────────────────────────────

/** Owner-role row — full fields (per Faris Decision 4 PDPL projection). */
export interface SalonOwnerAppointmentRowDto {
  id: number;
  appointmentDate: string;           // ISO 8601 UTC
  branchLocalStartTime: string;      // "HH:mm" — UTC wall-clock (FE re-formats)
  durationMinutes: number;
  status: string;                    // "Booked" | "Arrived" | "Started" | "Completed" | "NoShow" | "Cancelled"
  paymentStatus: string;             // "Unpaid" | "Paid" | "Refunded"
  serviceName: string;
  amount: number;
  currencyIso: string;               // "AED" | "JOD"
  staffId: number | null;
  staffDisplayName: string;
  staffInitials: string;
  staffAvatarUrl: string | null;
  branchId: number;
  branchName: string;
  branchIsHQ: boolean;
  clientDisplayName: string;
  clientPhoneMasked: string;         // "+97150…4567" — always masked on owner DTO too for brevity
  clientPhoneFull: string;           // E.164 full — OWNER ONLY
  sourceType: 'marketplace' | 'walkin';
  canMarkPaid: boolean;
  paymentMethod: string | null;      // present when Paid
  paidAt: string | null;             // ISO UTC when Paid
  externalPaymentId: string | null;  // Stripe ref — OWNER ONLY
  gatewayResponse: string | null;    // OWNER ONLY
  platformFeeAmount: number;         // OWNER ONLY
  platformFeeRate: number;           // OWNER ONLY
  discountAmount: number | null;     // OWNER ONLY
  netAmount: number;                 // OWNER ONLY
  notes: string | null;
  // AB#2034 — set when this row is one leg of a customer package bundle booking.
  bundleBookingId?: number | null;
  bundleName?: string | null;
  // O1 (AB#2199) — backend-allowed owner actions (same rules as the status endpoint).
  allowedTransitions?: string[];
  // O1 (AB#2199) — Customer | Salon | SalonDeclined | Expired | Admin when cancelled.
  cancellationSource?: string | null;
}

/** Staff-role row — PII-stripped (no Stripe IDs, no fee fields, no full phone, no netAmount). */
export interface SalonStaffAppointmentRowDto {
  id: number;
  appointmentDate: string;
  branchLocalStartTime: string;
  durationMinutes: number;
  status: string;
  paymentStatus: string;
  serviceName: string;
  amount: number;
  currencyIso: string;
  staffId: number | null;
  staffDisplayName: string;
  staffInitials: string;
  staffAvatarUrl: string | null;
  branchId: number;
  branchName: string;
  branchIsHQ: boolean;
  clientDisplayName: string;
  clientPhoneMasked: string;
  sourceType: 'marketplace' | 'walkin';
  canMarkPaid: boolean;
  paymentMethod: string | null;
  paidAt: string | null;
  notes: string | null;
}

export type AppointmentListItem = SalonOwnerAppointmentRowDto | SalonStaffAppointmentRowDto;

export interface CountsByPaymentStatus {
  paid: number;
  unpaid: number;
  refunded: number;
}

export interface AppointmentsListResponse {
  itemsForOwner: SalonOwnerAppointmentRowDto[];
  itemsForStaff: SalonStaffAppointmentRowDto[];
  cursor: string;
  hasMore: boolean;
  pageSize: number;
  counts: CountsByPaymentStatus;
}

// ─────────────────────────────────────────────────────────────────────────────
// Query params
// ─────────────────────────────────────────────────────────────────────────────

export interface AppointmentsListParams {
  status?: string;           // CSV e.g. "Booked,Arrived"
  paymentStatus?: string;    // CSV e.g. "Unpaid"
  from?: string;             // ISO date
  to?: string;               // ISO date
  branchIds?: string;        // CSV e.g. "1,2,3"
  staffIds?: string;         // CSV e.g. "5"
  cursor?: string;           // opaque cursor from prior page
  pageSize?: number;
  /** CR-2026-05-30 — free-text search across customer name + phone. */
  search?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// mark-paid types
// ─────────────────────────────────────────────────────────────────────────────

export type MarkPaidPaymentMethod = 'Cash' | 'Card' | 'BankTransfer' | 'Other';

export interface MarkPaidBody {
  paymentMethod: MarkPaidPaymentMethod;
}

export interface MarkPaidResponse {
  appointmentId: number;
  paymentId: number;
  transactionId: string;
  paymentStatus: string;
  paymentMethod: string;
  paidAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// API calls
// ─────────────────────────────────────────────────────────────────────────────

/** GET /salon-owner/appointments/list-v2 */
export async function getSalonOwnerAppointments(
  params: AppointmentsListParams,
  token: string,
): Promise<AppointmentsListResponse> {
  const { data } = await axios.get<AppointmentsListResponse>(
    `${API_BASE}/salon-owner/appointments/list-v2`,
    {
      params,
      headers: { Authorization: `Bearer ${token}` },
    },
  );
  return data;
}

/** POST /salon-owner/appointments/{id}/mark-paid */
export async function markAppointmentPaid(
  appointmentId: number,
  body: MarkPaidBody,
  idempotencyKey: string,   // RFC 4122 v4 UUID — generated client-side per submit
  token: string,
): Promise<MarkPaidResponse> {
  const { data } = await axios.post<MarkPaidResponse>(
    `${API_BASE}/salon-owner/appointments/${appointmentId}/mark-paid`,
    body,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Idempotency-Key': idempotencyKey,
      },
    },
  );
  return data;
}
